import { useJob } from "@/contexts/job-context";
import { useFloatingPanel } from "@/contexts/floating-panel-context";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Pause, Play, X } from "lucide-react";

export default function FloatingJobStatus() {
  const { job, clearJob, setPaused } = useJob();
  const { state } = useFloatingPanel();
  const saveAction = state.saveAction;
  const aiEditNode = state.aiEditNode;

  const showJob = Boolean(job.type);
  const showSave = Boolean(saveAction?.visible);
  const showAiEdit = Boolean(aiEditNode);

  if (!showJob && !showSave && !showAiEdit) return null;

  const percent = showJob && job.total > 0 ? Math.round((job.completed / job.total) * 100) : 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-[320px] rounded-md border bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/75">
      {showJob && (
        <>
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <div className="text-sm font-medium truncate">{job.title || "Background Task"}</div>
            <button
              type="button"
              className="p-1 rounded hover:bg-muted"
              onClick={() => clearJob()}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-3 py-2 space-y-2">
            <div className="text-xs text-muted-foreground">
              {job.completed} / {job.total} • {percent}%
              {job.currentItem && (
                <div className="mt-1">
                  Generating: <span className="font-medium">{job.currentItem}</span>
                </div>
              )}
            </div>
            <Progress value={percent} />
            <div className="flex items-center justify-between gap-2 pt-1">
              {job.type === "sprite-pack" && job.characterId ? (
                <Link href={`/?characterId=${encodeURIComponent(job.characterId)}`}>
                  <Button size="sm" variant="outline">Open</Button>
                </Link>
              ) : <span />}
              {job.running ? (
                <Button size="sm" variant="outline" onClick={() => { job.onStop?.(); setPaused(true); }}>
                  <Pause className="h-4 w-4 mr-1" /> Stop
                </Button>
              ) : (
                <Button size="sm" onClick={() => { setPaused(false); job.onResume?.(); }}>
                  <Play className="h-4 w-4 mr-1" /> Resume
                </Button>
              )}
            </div>
          </div>
        </>
      )}
      {(showSave || showAiEdit) && (
        <div className={`px-3 py-3 ${showJob ? "border-t" : ""}`}>
          <div className="flex flex-col gap-2">
            {showSave && (
              <Button
                size="sm"
                variant="destructive"
                className="w-full justify-center"
                disabled={saveAction?.disabled || !saveAction?.onClick}
                onClick={saveAction?.onClick}
                title={saveAction?.title}
              >
                {saveAction?.icon ? <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">{saveAction.icon}</span> : null}
                {saveAction?.label || "Save"}
              </Button>
            )}
            {showAiEdit ? <div className="w-full">{aiEditNode}</div> : null}
          </div>
        </div>
      )}
    </div>
  );
}
